import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  conversationControllerListConversationsQueryKey,
  useConversationControllerCreateConversation,
} from 'korint-sdk';
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputFooter,
} from '../../components/ai-elements/prompt-input';
import { Suggestions, Suggestion } from '../../components/ai-elements/suggestion';
import { MessageSquareIcon } from 'lucide-react';
import { BannerConversation } from './BannerConversation';
import { ContextLabelBadge } from '../FloatingChatModule/ContextLabelBadge';
import { useAIChatAnalytics } from '../FloatingChatModule/useAIChatAnalytics';
import { useContextLabel } from '../FloatingChatModule/useContextLabel';
import { Center, Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

export function NewConversationForm({
  onConversationCreated,
  urlContext,
  source,
}: {
  onConversationCreated: (conversationId: string, initialMessage: string) => void;
  urlContext?: Record<string, string>;
  source?: string;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const queryClient = useQueryClient();
  const analytics = useAIChatAnalytics();
  const { displayName, isLoading, isDismissed, dismiss, prefillString, hasContext } =
    useContextLabel(urlContext);

  const { mutateAsync: createConversation, isPending: isCreating } =
    useConversationControllerCreateConversation({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: conversationControllerListConversationsQueryKey(),
          });
        },
      },
    });

  const isCustomerContext = hasContext && !isDismissed;
  const rawSuggestions = t(
    isCustomerContext ? 'conversation.suggestions.customer' : 'conversation.suggestions.generic',
    { returnObjects: true },
  );
  const suggestions = Array.isArray(rawSuggestions)
    ? rawSuggestions.filter((s): s is string => typeof s === 'string')
    : [];
  const showSuggestions =
    !isCreating && (isCustomerContext || !hasContext) && suggestions.length > 0;

  const submitMessage = useCallback(
    async (text: string) => {
      const fullText = prefillString + text;
      // Keep the typed text visible (textarea is disabled while isCreating)
      // until ConversationThread mounts and renders the optimistic bubble.
      const result = await createConversation({
        data: {
          ...(source ? { metadata: { source } } : {}),
          locale: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      onConversationCreated(result.conversationId, fullText);
    },
    [createConversation, onConversationCreated, source, prefillString],
  );

  const handleSuggestionClick = (suggestion: string) => {
    analytics.trackSuggestionClicked({
      source: source ?? 'unknown',
      suggestion_text: suggestion,
      suggestion_type: isCustomerContext ? 'customer' : 'generic',
    });
    setInput(suggestion);
    void submitMessage(suggestion);
  };

  return (
    <Stack gap={0} className="h-full">
      <Center className="flex-1">
        <Stack gap={2} className="text-center">
          <MessageSquareIcon className="size-12 mx-auto text-muted-foreground" />
          <Title order={3} size="sm" className="font-medium">
            {t('conversation.newConversation')}
          </Title>
          <Text size="xs" tone="muted">
            {t('conversation.startDescription')}
          </Text>
        </Stack>
      </Center>
      <BannerConversation />
      {showSuggestions && (
        <Suggestions className="mx-4 mb-2">
          {suggestions.map(suggestion => (
            <Suggestion key={suggestion} suggestion={suggestion} onClick={handleSuggestionClick} />
          ))}
        </Suggestions>
      )}
      <PromptInput onSubmit={message => void submitMessage(message.text)} className="mx-4 mb-4">
        {hasContext && !isDismissed && (
          <ContextLabelBadge displayName={displayName} isLoading={isLoading} onDismiss={dismiss} />
        )}
        <PromptInputTextarea
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={t('conversation.inputPlaceholder')}
          disabled={isCreating}
        />
        <PromptInputFooter>
          <div />
          <PromptInputSubmit
            status={isCreating ? 'submitted' : 'ready'}
            disabled={!input.trim() || isCreating}
          />
        </PromptInputFooter>
      </PromptInput>
    </Stack>
  );
}
